import { useState, useMemo, useEffect } from 'react';

// Mimic the Appointment structure or use a compatible interface
interface Appointment {
    scheduled_time?: string | Date | null;
    scheduledTime?: string | Date | null;
    check_in_time?: string | Date | null;
    checkInTime?: string | Date | null;
    check_out_time?: string | Date | null;
    checkOutTime?: string | Date | null;
    actual_arrival_time?: string | Date | null;
    actualArrivalTime?: string | Date | null;
    [key: string]: any;
}

interface DetentionLogicResult {
    detentionHours: number;
    estimatedCost: number;
    status: string;
    isWorkIn: boolean;
    isTrafficImpacted: boolean;
    isDelayed: boolean;
}

/**
 * Optional warehouse-authoritative detention config.
 * Pass these from the component that has warehouse context.
 * Falls back to industry-standard defaults if not provided.
 */
interface DetentionConfig {
    /** Detention free time in minutes. Default: 120 (industry standard 2h) */
    detentionGraceMinutes?: number;
    /** Detention rate in $/hr. Default: 60 (industry standard) */
    detentionRate?: number;
    /** Check-in arrival grace in minutes (for work-in status). Default: 15 */
    arrivalGraceMinutes?: number;
}

// Industry-standard defaults (match warehouse DB schema defaults)
const DEFAULT_DETENTION_GRACE_MINUTES = 120;
const DEFAULT_DETENTION_RATE = 60;
const DEFAULT_ARRIVAL_GRACE_MINUTES = 15;

const fallback: DetentionLogicResult = {
    detentionHours: 0,
    estimatedCost: 0,
    status: 'idle',
    isWorkIn: false,
    isTrafficImpacted: false,
    isDelayed: false
};

// Helper: Safe Timestamp Extraction (Primitive Math Only)
const getTimestamp = (input: any): number | null => {
    if (!input) return null;
    const d = new Date(input);
    return isNaN(d.getTime()) ? null : d.getTime();
};

// Helper: Get Field (handling snake_case vs camelCase)
const getField = (obj: any, snake: string, camel: string) => obj ? (obj[snake] || obj[camel]) : null;

export const useDetentionLogic = (appointment: Appointment | null, config?: DetentionConfig): DetentionLogicResult => {
    // Warehouse-authoritative values with industry-standard fallbacks
    const detentionGraceMin = config?.detentionGraceMinutes ?? DEFAULT_DETENTION_GRACE_MINUTES;
    const detentionRate = config?.detentionRate ?? DEFAULT_DETENTION_RATE;
    const arrivalGraceMin = config?.arrivalGraceMinutes ?? DEFAULT_ARRIVAL_GRACE_MINUTES;

    // 1. Unconditional Hooks: State
    const [detentionState, setDetentionState] = useState<DetentionLogicResult>(fallback);

    // 2. Computed Timestamps (Primitive Math only - No date-fns here)
    const computedData = useMemo(() => {
        if (!appointment) return null;

        const scheduledRaw = getField(appointment, 'scheduled_time', 'scheduledTime') || getField(appointment, 'startAt', 'start_at');
        const checkInRaw = getField(appointment, 'check_in_time', 'checkInTime');
        const checkOutRaw = getField(appointment, 'check_out_time', 'checkOutTime');
        const actualArrivalRaw = getField(appointment, 'actual_arrival_time', 'actualArrivalTime') || checkInRaw;

        const scheduledTs = getTimestamp(scheduledRaw);
        const actualTs = getTimestamp(actualArrivalRaw);
        const checkOutTs = getTimestamp(checkOutRaw);

        return { scheduledTs, actualTs, checkOutTs };
    }, [appointment]);

    // 3. Logic Effect
    useEffect(() => {
        // Safe-guard: if no data, fallback
        if (!computedData) {
            setDetentionState(fallback);
            return;
        }

        const { scheduledTs, actualTs, checkOutTs } = computedData;

        let newState = { ...fallback };

        // 3a. Status Logic (Primitive Math)
        if (scheduledTs && actualTs) {
            // Calculate deadline: scheduled + arrival grace (in ms)
            const graceMs = arrivalGraceMin * 60 * 1000;
            const arrivalDeadline = scheduledTs + graceMs;

            const isWorkIn = actualTs > arrivalDeadline;
            const diffMs = actualTs - scheduledTs;

            newState.isWorkIn = isWorkIn;
            newState.isDelayed = diffMs > (15 * 60 * 1000);
            newState.status = isWorkIn ? 'WORK-IN' : 'ON_TIME';

            if (isWorkIn) {
                setDetentionState(newState);
                return;
            }
        } else {
            newState.status = 'PENDING';
            setDetentionState(newState);
            return;
        }

        // 3b. Detention Calculation
        // Uses warehouse-authoritative detentionGraceMinutes (no hardcoded values)
        const nowTs = Date.now();
        const effectiveEndTs = checkOutTs || nowTs;

        if (actualTs) {
            // Detention clock starts at max(arrival, scheduled)
            // Early arrivals don't start the clock before the appointment window
            const clockStart = scheduledTs ? Math.max(actualTs, scheduledTs) : actualTs;
            const dwellMs = effectiveEndTs - clockStart;
            const dwellMinutes = Math.floor(dwellMs / 60000);

            // Billable minutes = max(0, dwell - detentionGrace)
            const billableMinutes = Math.max(0, dwellMinutes - detentionGraceMin);
            const detentionHrs = billableMinutes / 60;

            newState.detentionHours = Number(detentionHrs.toFixed(2));
            newState.estimatedCost = Number((detentionHrs * detentionRate).toFixed(2));

            if (newState.detentionHours > 0) {
                newState.status = 'DETENTION_ACCRUING';
            }
        }

        setDetentionState(newState);

    }, [computedData, detentionGraceMin, detentionRate, arrivalGraceMin]);

    return detentionState;
};

